/**
 * Revenue Components - Organization and Platform earnings display
 */

import React from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  PiggyBank,
  Clock,
  CheckCircle,
  Info,
  ArrowRight,
  BarChart3
} from 'lucide-react';

// =============================================
// ORGANIZATION EARNINGS SUMMARY
// =============================================

export function OrganizationEarningsSummary({ data, className = '' }) {
  if (!data) return null;

  return (
    <div className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-white">Your Earnings</h3>
          <p className="text-sm text-white/40 mt-0.5">Revenue share from platform monetization</p>
        </div>
        <div className="p-3 rounded-xl bg-emerald-500/10">
          <DollarSign className="w-6 h-6 text-emerald-400" />
        </div>
      </div>
      
      {/* Main earnings display */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/10">
          <p className="text-3xl font-bold text-emerald-400">${data.total_earnings?.toFixed(2) || '0.00'}</p>
          <p className="text-sm text-white/50 mt-1">Total Earnings</p>
        </div>
        <div className="p-4 rounded-xl bg-white/[0.02]">
          <p className="text-2xl font-semibold text-amber-400">${data.pending_earnings?.toFixed(2) || '0.00'}</p>
          <p className="text-sm text-white/50 mt-1 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Pending
          </p>
        </div>
        <div className="p-4 rounded-xl bg-white/[0.02]">
          <p className="text-2xl font-semibold text-white">${data.paid_earnings?.toFixed(2) || '0.00'}</p>
          <p className="text-sm text-white/50 mt-1 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" /> Paid Out
          </p>
        </div>
      </div>
      
      {/* Earnings breakdown */}
      {data.breakdown && data.breakdown.length > 0 && (
        <div className="border-t border-white/[0.06] pt-4">
          <h4 className="text-sm font-medium text-white/70 mb-3">Earnings Breakdown</h4>
          <div className="space-y-2">
            {data.breakdown.map(item => (
              <div key={item.event_type} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="text-sm text-white/70">{formatEventType(item.event_type)}</span>
                  <span className="text-xs text-white/40">({item.event_count} events)</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-white">${item.earnings?.toFixed(2)}</span>
                  <span className="text-xs text-white/40 ml-2">of ${item.gross_revenue?.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Share percentage indicator */}
      <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center justify-between">
        <span className="text-sm text-white/50">Your share</span>
        <span className="text-sm font-medium text-emerald-400">{data.split_percentage || 70}%</span>
      </div>
    </div>
  );
}

// =============================================
// PLATFORM EARNINGS SUMMARY
// =============================================

export function PlatformEarningsSummary({ data, className = '' }) {
  if (!data) return null;

  return (
    <div className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-white">Platform Revenue</h3>
          <p className="text-sm text-white/40 mt-0.5">Total monetization performance</p>
        </div>
        <div className="p-3 rounded-xl bg-purple-500/10">
          <TrendingUp className="w-6 h-6 text-purple-400" />
        </div>
      </div>
      
      {/* Revenue flow visualization */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-xl bg-white/[0.03] text-center">
          <p className="text-2xl font-bold text-white">${data.total_gross_revenue?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-white/50 mt-1">Gross Revenue</p>
        </div>
        <div className="flex items-center justify-center">
          <ArrowRight className="w-6 h-6 text-white/20" />
        </div>
        <div className="grid grid-rows-2 gap-2">
          <div className="p-2 rounded-lg bg-purple-500/10 text-center">
            <p className="text-lg font-semibold text-purple-400">${data.platform_earnings?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-white/50">Platform ({data.platform_percentage || 30}%)</p>
          </div>
          <div className="p-2 rounded-lg bg-emerald-500/10 text-center">
            <p className="text-lg font-semibold text-emerald-400">${data.organization_payouts?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-white/50">Orgs ({100 - (data.platform_percentage || 30)}%)</p>
          </div>
        </div>
      </div>
      
      {/* Breakdown by type */}
      {data.breakdown && data.breakdown.length > 0 && (
        <div className="border-t border-white/[0.06] pt-4">
          <h4 className="text-sm font-medium text-white/70 mb-3">Revenue by Type</h4>
          <div className="space-y-2">
            {data.breakdown.map(item => (
              <div key={item.event_type} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-purple-400" />
                  <span className="text-sm text-white/70">{formatEventType(item.event_type)}</span>
                  <span className="text-xs text-white/40">({item.event_count})</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-white">${item.gross_revenue?.toFixed(2)}</span>
                  <span className="text-xs text-purple-400 ml-2">(+${item.platform_earnings?.toFixed(2)})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Top organizations */}
      {data.top_organizations && data.top_organizations.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/[0.06]">
          <h4 className="text-sm font-medium text-white/70 mb-3">Top Revenue Organizations</h4>
          <div className="space-y-2">
            {data.top_organizations.slice(0, 5).map((org, i) => (
              <div key={org.organization_id} className="flex items-center justify-between">
                <span className="text-sm text-white/60">#{i + 1} {org.organization_id.slice(0, 8)}...</span>
                <span className="text-sm font-medium text-white">${org.gross_revenue?.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================
// REVENUE EVENT HISTORY
// =============================================

export function RevenueEventHistory({ events, onLoadMore, className = '' }) {
  if (!events || events.length === 0) {
    return (
      <div className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-8 text-center ${className}`}>
        <BarChart3 className="w-10 h-10 text-white/20 mx-auto mb-3" />
        <p className="text-white/40">No revenue events yet</p>
        <p className="text-white/30 text-sm mt-1">Events will appear here as monetization occurs</p>
      </div>
    );
  }

  return (
    <div className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] overflow-hidden ${className}`}>
      <div className="p-4 border-b border-white/[0.06]">
        <h3 className="font-medium text-white">Revenue Events</h3>
      </div>
      
      <div className="divide-y divide-white/[0.04]">
        {events.map(event => (
          <div key={event.id} className="p-4 hover:bg-white/[0.02] transition-colors">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${getStatusColor(event.status)}`} />
                <span className="text-sm font-medium text-white">{formatEventType(event.event_type)}</span>
              </div>
              <span className="text-sm font-medium text-white">${event.gross_amount?.toFixed(4)}</span>
            </div>
            
            <div className="flex items-center justify-between text-xs text-white/50">
              <div className="flex items-center gap-4">
                <span>Platform: ${event.platform_amount?.toFixed(4)}</span>
                <span>Org: ${event.organization_amount?.toFixed(4)}</span>
              </div>
              <span>{formatDate(event.created_at)}</span>
            </div>
            
            {event.placement_key && (
              <div className="mt-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.06] text-white/50">
                  {event.placement_key}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {onLoadMore && (
        <div className="p-4 border-t border-white/[0.06]">
          <button 
            onClick={onLoadMore}
            className="w-full py-2 text-sm text-white/50 hover:text-white transition-colors"
          >
            Load more events
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================
// REVENUE SPLIT EXPLANATION
// =============================================

export function RevenueSplitExplanation({ data, className = '' }) {
  if (!data) return null;

  return (
    <div className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-6 ${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-blue-500/10">
          <Info className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h3 className="font-medium text-white">{data.model}</h3>
          <p className="text-sm text-white/50">{data.description}</p>
        </div>
      </div>
      
      {/* Visual split */}
      <div className="mb-6">
        <div className="flex rounded-lg overflow-hidden h-8">
          <div 
            className="bg-purple-500/40 flex items-center justify-center text-sm text-white font-medium"
            style={{ width: `${data.splits?.platform?.percentage || 30}%` }}
          >
            {data.splits?.platform?.percentage || 30}%
          </div>
          <div 
            className="bg-emerald-500/40 flex items-center justify-center text-sm text-white font-medium"
            style={{ width: `${data.splits?.organization?.percentage || 70}%` }}
          >
            {data.splits?.organization?.percentage || 70}%
          </div>
        </div>
        <div className="flex justify-between mt-2 text-xs text-white/50">
          <span>{data.splits?.platform?.label || 'Platform Fee'}</span>
          <span>{data.splits?.organization?.label || 'Your Earnings'}</span>
        </div>
      </div>
      
      {/* Split details */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/10">
          <h4 className="text-sm font-medium text-purple-400 mb-1">{data.splits?.platform?.label}</h4>
          <p className="text-xs text-white/50">{data.splits?.platform?.description}</p>
        </div>
        <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
          <h4 className="text-sm font-medium text-emerald-400 mb-1">{data.splits?.organization?.label}</h4>
          <p className="text-xs text-white/50">{data.splits?.organization?.description}</p>
        </div>
      </div>
      
      {/* Revenue types */}
      {data.revenue_types && (
        <div className="border-t border-white/[0.06] pt-4">
          <h4 className="text-sm font-medium text-white/70 mb-3">Revenue Types</h4>
          <div className="grid grid-cols-2 gap-2">
            {data.revenue_types.map(type => (
              <div key={type.type} className="p-3 rounded-lg bg-white/[0.02]">
                <p className="text-sm font-medium text-white">{type.label}</p>
                <p className="text-xs text-white/40 mt-0.5">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Payout info */}
      {data.payout_info && (
        <div className="mt-4 pt-4 border-t border-white/[0.06]">
          <h4 className="text-sm font-medium text-white/70 mb-2">Payout Information</h4>
          <div className="flex items-center gap-4 text-sm text-white/50">
            <span>Min: ${data.payout_info.minimum_payout}</span>
            <span>•</span>
            <span>{data.payout_info.payout_schedule}</span>
            <span>•</span>
            <span>{data.payout_info.payout_methods?.join(', ')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================
// HELPERS
// =============================================

function formatEventType(type) {
  const labels = {
    'ad_impression': 'Ad Impression',
    'ad_click': 'Ad Click',
    'sponsorship_fee': 'Sponsorship',
    'ticket_fee': 'Ticket Fee',
    'promoted_listing': 'Promoted Listing',
    'subscription_share': 'Subscription',
    'other': 'Other'
  };
  return labels[type] || type;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function getStatusColor(status) {
  const colors = {
    'pending': 'bg-amber-400',
    'processed': 'bg-blue-400',
    'paid': 'bg-emerald-400',
    'cancelled': 'bg-red-400'
  };
  return colors[status] || 'bg-gray-400';
}

export default {
  OrganizationEarningsSummary,
  PlatformEarningsSummary,
  RevenueEventHistory,
  RevenueSplitExplanation
};
